import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../translations/LanguageContext';
import { services } from '../mock/mockData';
import { Card } from './ui/card';
import { ArrowLeft, Clock, CheckCircle2 } from 'lucide-react';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const service = services.find((s) => s.id === serviceId);

  // Scroll to top when component mounts or serviceId changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return <div className="min-h-screen flex items-center justify-center">Service not found</div>;
  }

  // Check if translations are loaded
  if (!t || !t.services || !t.serviceDetails) {
    return <div className="min-h-screen flex items-center justify-center">Loading translations...</div>;
  }

  const scrollToBooking = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('booking');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const scrollToServices = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('services');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Get service translations based on serviceId
  const getServiceTranslations = () => {
    try {
      if (serviceId === 'acupuncture') {
        return {
          name: t.services.acupuncture.name,
          subtitle: t.services.acupuncture.shortDesc,
          detail: t.serviceDetails?.acupuncture || {}
        };
      } else if (serviceId === 'aculifting') {
        return {
          name: t.services.aculifting.name,
          subtitle: t.services.aculifting.shortDesc,
          detail: t.serviceDetails?.aculifting || {}
        };
      } else if (serviceId === 'korean-relaxation') {
        return {
          name: t.services.koreanRelaxation.name,
          subtitle: t.services.koreanRelaxation.shortDesc,
          detail: t.serviceDetails?.koreanRelaxation || {}
        };
      }
    } catch (error) {
      console.error('Translation error:', error);
      return null;
    }
    return null;
  };

  const serviceTranslations = getServiceTranslations();
  if (!serviceTranslations) {
    console.error('No translations found for service:', serviceId);
    return <div className="min-h-screen flex items-center justify-center">Translation error</div>;
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4">
      <div className="container max-w-5xl mx-auto">
        <button
          onClick={scrollToServices}
          className="flex items-center gap-2 text-accent-text hover:text-accent-strong mb-8 transition-colors">
          <ArrowLeft size={20} />
          <span className="body-medium font-medium">{t.nav.services}</span>
        </button>

        <div className="relative overflow-hidden rounded-2xl mb-8 h-96">
          <img
            src={service.image}
            alt={serviceTranslations.name}
            className="w-full h-full object-cover"
            style={serviceId === 'korean-relaxation' ? { objectPosition: '30% center' } : {}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
            <div className="p-8">
              <h1 className="heading-1 mb-2 !text-[#00C73E]">{serviceTranslations.name}</h1>
              <p className="body-large service-hero-subtitle">{serviceTranslations.subtitle}</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-1 gap-6 mb-12">
          <Card className="product-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="text-accent-primary" size={24} />
              <h3 className="heading-3">{t.serviceDetails?.duration || 'Duration'}</h3>
            </div>
            <p className="body-large">{service.duration}</p>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="heading-2 mb-6">{t.serviceDetails?.aboutTreatment || 'About This Treatment'}</h2>
          <p className="body-large mb-6">{serviceTranslations.detail?.aboutText || service.fullDescription}</p>
        </div>

        {/* Therapeutic Indications (Acupuncture only) */}
        {serviceId === 'acupuncture' && serviceTranslations.detail?.indications && (
          <div className="mb-12">
            <h2 className="heading-2 mb-6">{t.serviceDetails?.therapeuticIndications || 'Therapeutic Indications:'}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {serviceTranslations.detail.indications.map((indication, idx) => (
                <Card key={idx} className="product-card p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={24} className="text-accent-primary mt-1 flex-shrink-0" />
                    <p className="body-medium">{indication}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Benefits (Acu-lifting and Korean Relaxation) */}
        {(serviceId === 'aculifting' || serviceId === 'korean-relaxation') && serviceTranslations.detail?.benefitsList && (
          <div className="mb-12">
            <h2 className="heading-2 mb-6">{t.serviceDetails?.benefits || 'Benefits:'}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {serviceTranslations.detail.benefitsList.map((benefit, idx) => (
                <Card key={idx} className="product-card p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={24} className="text-accent-primary mt-1 flex-shrink-0" />
                    <p className="body-medium">{benefit}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* What to Expect */}
        {serviceTranslations.detail?.expectSteps && (
          <div className="mb-12">
            <h2 className="heading-2 mb-6">{t.serviceDetails?.whatToExpect || 'What to Expect'}</h2>
            <Card className="product-card p-8">
              <div className="space-y-4">
                <p className="body-medium">{serviceTranslations.detail?.expectIntro || ''}</p>
                <ul className="space-y-3">
                  {serviceTranslations.detail.expectSteps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="text-accent-primary font-bold">{idx + 1}.</span>
                      <span className="body-medium">
                        <strong>{step.title}</strong> {step.desc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center bg-accent-wash rounded-2xl p-12">
          <h2 className="heading-2 mb-4">{t.serviceDetails?.readyToBook || 'Ready to Book Your Appointment?'}</h2>
          <p className="body-large text-secondary mb-8 max-w-2xl mx-auto">
            {t.serviceDetails?.bookingCTA || 'Take the first step towards better health and wellness. Schedule your'} {serviceTranslations.name.toLowerCase()} {t.serviceDetails?.sessionToday || 'session today.'}
          </p>
          <button onClick={scrollToBooking} className="btn-primary">
            {t.nav?.bookAppointment || 'Book Appointment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
